const HERO_IMAGE =
  'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=1600&q=80';

const RELATED_GUIDES = [
  {
    slug: 'soccer-field-injury-remedies-calf-hamstring',
    title: 'On-Field Remedies for Soccer Calf, Soleus & Hamstring Injuries',
    blurb: 'first aid for other common pitch injuries.',
  },
  {
    slug: 'tennis-elbow-injury-remedies-exercises',
    title: 'Tennis Elbow: Remedies, Stretches & Rehab Exercises',
    blurb: 'another overuse rehab plan with video demos.',
  },
  {
    slug: 'soleus-pain-kt-tape-soccer-guide',
    title: 'Soleus Pain in Soccer: How to Use KT Tape & Keep Playing Safely',
    blurb: 'load management when you are itching to return.',
  },
  {
    slug: '7v7-soccer-formation-over-35-guide',
    title: '7v7 Soccer Formation Guide for Players 35+',
    blurb: 'pace and recovery-aware tactics once you are back.',
  },
];

const REFERENCE_LINKS = [
  {
    label: 'American Academy of Orthopaedic Surgeons — low back pain',
    url: 'https://orthoinfo.aaos.org/en/diseases--conditions/lowback-pain/',
  },
  {
    label: 'NHS — back pain self-help and when to get help',
    url: 'https://www.nhs.uk/conditions/back-pain/',
  },
  {
    label: 'FIFA 11+ injury prevention warm-up',
    url: 'https://inside.fifa.com/health-and-medical/injury-prevention',
  },
  {
    label: 'Ask Doctor Jo — lower back pain playlist overview',
    url: 'https://www.askdoctorjo.com/lower-back-pain-relief/',
  },
];

/** 7-day lower back recovery plan for soccer and other sports */
export const LOWER_BACK_RECOVERY_7_DAY_POST = {
  id: 'featured-lower-back-recovery-7-day',
  slug: '7-day-lower-back-pain-recovery-soccer-sports',
  title: '7-Day Lower Back Pain Recovery Plan for Soccer & Other Sports',
  excerpt:
    'Tweaked your lower back after a match, gym session, or long run? Follow this calm 7-day return plan—relative rest, gentle mobility, McGill-style core work, and video demos—before you sprint again. Not medical advice.',
  publishedAt: '2026-08-08T18:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Player Wellness',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'Related training & wellness guides',
  relatedPosts: RELATED_GUIDES,
  referenceLinks: REFERENCE_LINKS,
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '7 days', label: 'Calm → mobilize → stabilize' },
        { value: 'Walk daily', label: 'Better than bed rest for most flares' },
        { value: 'McGill Big 3', label: 'Core stability focus' },
        { value: 'See a clinician', label: 'If red flags or pain worsens' },
      ],
    },
    {
      type: 'panel',
      title: 'Who this 7-day plan is for',
      paragraphs: [
        'This guide is for **mild-to-moderate** lower back stiffness or ache after soccer, running, lifting, or other sports—think a sudden twist for a tackle, a long match on hard ground, deadlifts with a rounded spine, or sitting all week then exploding into play.',
        'It is a **self-guided calm-down and restart**, not treatment for a fracture, disc emergency, or nerve injury. If your pain is severe, getting worse, or matches any red flag below, stop this plan and get examined in person.',
        'The goal by Day 7 is **comfortable daily walking, light core stability work, and a plan to ease back into training**—not full match fitness.',
      ],
      chips: ['Soccer & multi-sport', 'Home recovery', 'Video demos', 'Not medical advice'],
    },
    {
      type: 'panel',
      title: 'Red flags — skip DIY and get help now',
      list: [
        'Pain after a hard fall, collision, or suspected fracture.',
        'Numbness, tingling, or weakness in a leg that is new or spreading.',
        'Loss of bladder or bowel control, or numbness in the saddle area.',
        'Fever, unexplained weight loss, or night pain that wakes you every night.',
        'Pain so sharp you cannot walk or stand for more than a minute.',
        'History of cancer, osteoporosis, long-term steroid use, or IV drug use with new back pain.',
      ],
      callout:
        'Emergency or same-day care beats “pushing through.” This article cannot diagnose your injury.',
    },
    {
      type: 'table',
      title: 'How sports often irritate the lower back',
      intro: 'Matching the trigger helps you choose what to avoid for a few days.',
      headers: ['Trigger', 'Common in', 'What usually helps first'],
      rows: [
        [
          'Sudden twist / awkward landing',
          'Soccer tackles, headers battles, court sports',
          'Short walks, avoid twisting drills, gentle pelvic tilts',
        ],
        [
          'Fatigue + long sitting after play',
          'Adult leagues, long car rides home',
          'Change positions often, walk breaks, hip flexor gentle stretch',
        ],
        [
          'Heavy lifting with rounded spine',
          'Gym, deadlifts, odd-object carries',
          'Pause loaded flexion; rebuild brace with McGill Big 3',
        ],
        [
          'Hard sprinting / kicking when cold',
          'Soccer, track, cross country',
          'Extra warm-up later; no max sprints in week 1',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Recovery rules for the whole week',
      orderedList: [
        '**Move a little, often** — total bed rest usually makes backs stiffer. Prefer short easy walks over one long outing.',
        '**Pain guide** — keep sessions at roughly 0–3/10. Mild ache that settles within 24 hours is OK; sharp or next-day spikes mean you did too much.',
        '**Avoid for Days 1–4** — sit-ups, toes-to-bar, heavy deadlifts, max sprints, long drives without breaks, and “cracking” your back repeatedly.',
        '**Ice or heat** — ice 10–15 minutes if it feels inflamed after a flare; heat for stiff morning backs. Use a barrier and common sense.',
        '**Sleep** — side-lying with a pillow between knees, or on your back with a pillow under knees, often feels better than stomach sleeping.',
        '**Breathing** — before every exercise, gently brace as if preparing for a light cough—then breathe. Do not hold your breath and strain.',
      ],
    },
    {
      type: 'videoList',
      title: 'Video — gentle relief stretches (start here)',
      intro:
        'Use these for form cues on Days 1–3. Follow along lightly; skip any move that shoots pain into a leg.',
      videos: [
        {
          title: '10 Best Lower Back Stretches for Low Back Pain Relief',
          channel: 'Ask Doctor Jo',
          description:
            'Pelvic tilts, cat/dog, trunk rotation, knee-to-chest, and other gentle openers for general low-back stiffness.',
          url: 'https://www.youtube.com/watch?v=Xm8iSSdU3I0',
        },
        {
          title: 'Back Pain Relief Exercises & Stretches',
          channel: 'Ask Doctor Jo',
          description:
            'Pelvic tilts, bridges, knee-to-chest, and progressive mobility you can sample across the week.',
          url: 'https://www.youtube.com/watch?v=2VuLBYrgG94',
        },
        {
          title: 'Back Pain Relief Stretches — 5 Minute Real-Time Routine',
          channel: 'Ask Doctor Jo',
          description:
            'Short real-time routine when you only have a few minutes between meetings or after a match.',
          url: 'https://www.youtube.com/watch?v=UYMmtEFhuxA',
        },
      ],
    },
    {
      type: 'dayBlocks',
      title: 'Your 7-day recovery plan',
      intro:
        'Do the listed movement 1–2 times per day. Keep soccer/training off until Day 6–7 light return steps—and only if symptoms are clearly settling.',
      days: [
        {
          title: 'Day 1 — Calm the flare',
          meals: [
            'Goal: reduce irritation without shutting down completely.',
            'Walk: 5–10 minutes easy, 3–4 times through the day.',
            'Mobility: pelvic tilts 8–10 reps; single knee-to-chest 20–30 seconds each side (2 rounds).',
            'Avoid: running, gym lifting, long sitting without breaks.',
            'Check-in: can you walk around the block by evening with ≤3/10 pain?',
          ],
        },
        {
          title: 'Day 2 — Keep blood flowing',
          meals: [
            'Goal: same calm load, slightly smoother mobility.',
            'Walk: 10–15 minutes, 2–3 times.',
            'Mobility: add gentle cat-cow 6–8 slow reps; supine trunk rotations 5 each side.',
            'Optional: 10 minutes heat before mobility if mornings are stiff.',
            'Avoid: twisting soccer drills, yoga deep backbends, sit-ups.',
          ],
        },
        {
          title: 'Day 3 — Introduce light stability',
          meals: [
            'Goal: teach the spine to stay steady while limbs move.',
            'Walk: 15–20 minutes continuous if pain stays mild.',
            'Stability: dead-bug holds (opposite arm/leg) 6 slow reps each side; glute bridge 8–10 easy reps.',
            'Mobility: repeat Day 2 openers as a warm-up.',
            'Stop rule: if pain radiates below the knee, drop back to Day 1–2 only.',
          ],
        },
        {
          title: 'Day 4 — McGill Big 3 foundation',
          meals: [
            'Goal: start the classic spine-stability trio with short holds.',
            'Warm-up: 5 minutes walk + cat-cow.',
            'McGill Big 3: curl-up, side plank (knees OK), bird dog — try 4–6 holds of ~8–10 seconds each.',
            'Walk: 20 minutes easy later in the day.',
            'Avoid: still no max sprints or heavy posterior-chain lifting.',
          ],
        },
        {
          title: 'Day 5 — Build endurance in the brace',
          meals: [
            'Goal: same Big 3 with slightly better control, not heavier load.',
            'McGill Big 3: 2 short sets (for example 6 then 4 holds) focusing on quiet hips and steady breathing.',
            'Add: standing hip hinge pattern with no weight — soft knees, long spine, 8 reps.',
            'Walk or easy bike: 20–25 minutes.',
            'Optional: light upper-body gym if it does not aggravate the back.',
          ],
        },
        {
          title: 'Day 6 — Sport-specific prep (still easy)',
          meals: [
            'Goal: prepare tissues for a return without match intensity.',
            'Warm-up: FIFA 11+ style light jog + dynamic hips (no hard cutting yet).',
            'Stability: full Big 3 once; then bodyweight squats to a comfortable depth 2×8.',
            'Skills (soccer): passing in a small grid, no headers battles or 50/50 tackles.',
            'Other sports: easy technical work only — no race pace or max lifts.',
          ],
        },
        {
          title: 'Day 7 — Decision day for return',
          meals: [
            'Goal: honest readiness check, then a graded return plan.',
            'Test: walk briskly 20 minutes, do Big 3 once, and attempt 4–6 progressive accelerations (not full sprint).',
            'Green light: pain ≤2/10 during and no next-morning spike → resume light team training midweek.',
            'Yellow light: lingering stiffness → repeat Days 4–6 for another 3–4 days.',
            'Red light: radiating pain or weakness → clinician visit before any competitive play.',
          ],
        },
      ],
    },
    {
      type: 'videoList',
      title: 'Video — McGill Big 3 core stability',
      intro:
        'Watch before Days 4–7. Short holds with clean form beat long shaky sets. Kneeling side planks are fine to start.',
      videos: [
        {
          title: 'Rehab Low Back Pain with the McGill Big 3',
          channel: 'Tutorial Tuesdays / Physio-style demo',
          description:
            'Clear demos of the curl-up, side plank, and bird dog with a descending-rep approach.',
          url: 'https://www.youtube.com/watch?v=0U2UN6QBzCM',
        },
        {
          title: 'The ULTIMATE McGill Big 3 Guide With Progressions',
          channel: 'Strength & rehab coaching',
          description:
            'Shows easier and harder versions so you can match Day 4–7 ability without forcing a full side plank.',
          url: 'https://www.youtube.com/watch?v=1E1h9S8Hp_M',
        },
        {
          title: 'The McGill Big 3 For Core Stability',
          channel: 'Clinical coaching demo',
          description:
            'Extra cues for bracing and the 6-4-2 hold pattern many clinicians teach for endurance.',
          url: 'https://www.youtube.com/watch?v=GmGoAijwf1s',
        },
      ],
    },
    {
      type: 'panel',
      title: 'Exercise cues that protect your back',
      list: [
        '**Pelvic tilt** — flatten the low back gently into the floor; do not force a crunch.',
        '**Cat-cow** — slow and small; stop if you get sharp pinching.',
        '**Glute bridge** — drive through heels, squeeze glutes, keep ribs quiet (no over-arching).',
        '**McGill curl-up** — hand under the natural low-back curve; lift head/shoulders slightly—do not do a full sit-up.',
        '**Side plank** — start from knees; hips stacked; stop if shoulder or back sharp-pains.',
        '**Bird dog** — reach long, not high; imagine a glass of water on your low back.',
      ],
      callout:
        'If a video shows a version that feels aggressive for your flare, choose the regression—not the hardest demo.',
    },
    {
      type: 'table',
      title: 'Return-to-sport checklist after Day 7',
      intro: 'Only progress when the previous level feels boringly easy.',
      headers: ['Stage', 'What to do', 'Usually ready when'],
      rows: [
        [
          '1. Daily life',
          'Walk, stairs, light chores',
          'No limp; sleep not wrecked by pain',
        ],
        [
          '2. Conditioning',
          'Easy jog / bike / swim',
          '20+ minutes with ≤2/10 symptoms',
        ],
        [
          '3. Skills',
          'Passing, shooting form, easy strides',
          'Can accelerate without catching',
        ],
        [
          '4. Team training',
          'Non-contact drills → limited contact',
          'Next-day soreness only, not sharp pain',
        ],
        [
          '5. Full match',
          'Game minutes',
          'Completed a full hard training week first',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Prevent the next flare',
      list: [
        'Warm up with FIFA 11+ or a 8–10 minute jog + dynamic hips before kickoff.',
        'Keep a mini Big 3 habit 3–4 days per week even when you feel “fine.”',
        'Break up long desk or drive sessions every 30–45 minutes.',
        'Progress gym loads with a braced hip hinge—leave ego PRs for healthy weeks.',
        'Sleep and hydration matter more than another “core shred” circuit.',
      ],
      callout:
        'Recurring back pain, especially with leg symptoms, deserves a physio or sports-medicine assessment—not endless YouTube hopping.',
    },
  ],
};
